import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import AdminLayout from "../../components/admin/AdminLayout"
import { toast } from "react-toastify"
import TempleService from "../../services/templeService"
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api"

const TempleForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = !!id

  const [loading, setLoading] = useState(isEditMode)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [formChanged, setFormChanged] = useState(false)
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }) // Default to center of India
  const [uploadingImage, setUploadingImage] = useState(false)


  // Form state
  const [formData, setFormData] = useState({
    name: "",
    deity: "",
    location: {
      address: "",
      city: "",
      state: "",
      country: "India",
      coordinates: {
        lat: 20.5937,
        lng: 78.9629,
      },
    },
    category: "",
    description: "",
    history: "",
    architecture: "",
    timings: "",
    festivals: [],
    images: [],
    mainImage: "/placeholder.svg?height=400&width=600",
    importance: "",
    visitingTips: "",
    nearbyAttractions: [],
    status: "draft",
    featured: false,
  })

  // Categories for dropdown
  const categories = [
    { id: "Shiva", name: "Shiva Temples" },
    { id: "Vishnu", name: "Vishnu Temples" },
    { id: "Devi", name: "Devi Temples" },
    { id: "Ganesha", name: "Ganesha Temples" },
    { id: "Other", name: "Other Temples" },
  ]

  // States for dropdown
  const states = [
    { id: "Uttarakhand", name: "Uttarakhand" },
    { id: "Jammu & Kashmir", name: "Jammu & Kashmir" },
    { id: "Andhra Pradesh", name: "Andhra Pradesh" },
    { id: "Punjab", name: "Punjab" },
    { id: "Tamil Nadu", name: "Tamil Nadu" },
    { id: "Uttar Pradesh", name: "Uttar Pradesh" },
    { id: "Odisha", name: "Odisha" },
    { id: "Gujarat", name: "Gujarat" },
    { id: "Delhi", name: "Delhi" },
    { id: "Maharashtra", name: "Maharashtra" },
    { id: "Karnataka", name: "Karnataka" },
    { id: "Kerala", name: "Kerala" },
    { id: "Rajasthan", name: "Rajasthan" },
    { id: "Bihar", name: "Bihar" },
    { id: "West Bengal", name: "West Bengal" },
  ]

  useEffect(() => {
    if (isEditMode) {
      const fetchTempleData = async () => {
        try {
          const response = await TempleService.getTempleById(id)

          if (response) {
            setFormData(response)
            if (response.mainImage) {
              setImagePreview(response.mainImage)
            }
            if (response.location && response.location.coordinates) {
              setMapCenter(response.location.coordinates)
            }
          }
        } catch (err) {
          setError("Failed to fetch temple data. Please try again.");
          console.error(err);
          toast.error("Failed to load temple data");
        } finally {
          setLoading(false)
        }
      }

      fetchTempleData()
    }

    // Set up beforeunload event to warn about unsaved changes
    const handleBeforeUnload = (e) => {
      if (formChanged) {
        e.preventDefault()
        e.returnValue = ""
        return ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [id, isEditMode, formChanged])

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target

    // Handle nested fields
    if (name.includes(".")) {
      const parts = name.split(".")

      if (parts.length === 2) {
        const [parent, child] = parts
        setFormData({
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: value,
          },
        })
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts
        setFormData({
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: {
              ...formData[parent][child],
              [grandchild]: value,
            },
          },
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }

    setFormChanged(true)
  }

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setFormData({
      ...formData,
      [name]: checked,
    })
    setFormChanged(true)
  }

  // Handle array fields (facilities, travel tips)
  const handleArrayChange = (index, field, value, arrayName) => {
    const updatedArray = [...formData[arrayName]]

    if (typeof updatedArray[index] === "string") {
      updatedArray[index] = value
    } else {
      updatedArray[index] = {
        ...updatedArray[index],
        [field]: value,
      }
    }

    setFormData({
      ...formData,
      [arrayName]: updatedArray,
    })

    setFormChanged(true)
  }

  // Add new item to array fields
  const handleAddArrayItem = (arrayName, template = {}) => {
    setFormData({
      ...formData,
      [arrayName]: [...formData[arrayName], template],
    })

    setFormChanged(true)
  }
 
  // Remove item from array fields
  const handleRemoveArrayItem = (index, arrayName) => {
    const updatedArray = [...formData[arrayName]]
    updatedArray.splice(index, 1)

    setFormData({
      ...formData,
      [arrayName]: updatedArray,
    })

    setFormChanged(true)
  }

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Preview the image
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file) 

    try {
      setUploadingImage(true)
      // In a real app, you would upload the image to your server/cloud storage
      const uploadResult = await TempleService.uploadTempleImage(file)
      const imageUrl = uploadResult?.imageUrl;
      console.log("Image Url From Cloudinary: ", imageUrl);

      if(imageUrl) {
        setFormData((prev) => ({
          ...prev,
          mainImage: imageUrl,
        }));
        toast.success('image set successfully!');
      }else {
        alert('Upload failed: ', uploadResult.message);
      }
      setUploadingImage(false)
      setFormChanged(true)
    } catch (error) {
      console.error("Error uploading image:", error)
      toast.error("Failed to upload image")
      setUploadingImage(false)
    }
  }

  // Handle map click to set coordinates
  const handleMapClick = (e) => {
    const newCoordinates = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    }

    setMapCenter(newCoordinates)
    setFormData({
      ...formData,
      location: {
        ...formData.location,
        coordinates: newCoordinates,
      },
    })

    setFormChanged(true)
  }

  // Validate form data
  const validateForm = () => {
    // Required fields
    if (!formData.name || !formData.description || !formData.deity || !formData.category) {
      toast.error("Please fill all required fields")
      return false
    }

    // Location validation
    if (!formData.location.address || !formData.location.city || !formData.location.state) {
      toast.error("Please provide complete location information")
      return false
    }

    return true
  }

  // Handle form submission
  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setSaving(true)

    try {
      const dataToSubmit = {
        ...formData,
        status: saveAsDraft ? "draft" : "published",
      }

      let response
      if (isEditMode) {
        response = await TempleService.updateTemple(id, dataToSubmit);
        toast.success("Temple Updated Successfully!");
      } else {
        response = await TempleService.createTemple(dataToSubmit);
        toast.success("Temple Created Successfully!");
      }

      // Reset form changed state
      setFormChanged(false);

      // Redirect to temple list
      navigate("/admin/temples")
    } catch (err) {
      console.error("Error saving temple:", err)
      toast.error(`Failed to ${isEditMode ? "update" : "create"} temple: ${err.message || "Unknown error"}`)
      setError(`Failed to ${isEditMode ? "update" : "create"} temple. Please try again.`)
    } finally {
      setSaving(false)
    }
  }

  // Handle cancel button
  const handleCancel = () => {
    if (formChanged) {
      setShowUnsavedChangesModal(true)
    } else {
      navigate("/admin/temples")
    }
  }

  if (loading) {
    return (
      <AdminLayout title={isEditMode ? "Edit Temple" : "Add New Temple"}>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-spiritual-600 mb-2"></div>
          <p className="text-gray-600">Loading temple data...</p>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title={isEditMode ? `Edit Temple: ${formData.name}` : "Add New Temple"}>
      {error && <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">{error}</div>}

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-8">
        {/* Basic Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Temple Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              />
            </div>

            <div>
              <label htmlFor="deity" className="block text-sm font-medium text-gray-700 mb-1">
                Deity <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="deity"
                name="deity"
                value={formData.deity}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              />
            </div>

            <div>
              <label htmlFor="location.address" className="block text-sm font-medium text-gray-700 mb-1">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="location.address"
                name="location.address"
                value={formData.location.address}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                placeholder="Street address"
              />
            </div>

            <div>
              <label htmlFor="location.city" className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="location.city"
                name="location.city"
                value={formData.location.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                placeholder="City"
              />
            </div>

            <div>
              <label htmlFor="location.state" className="block text-sm font-medium text-gray-700 mb-1">
                State <span className="text-red-500">*</span>
              </label>
              <select
                id="location.state"
                name="location.state"
                value={formData.location.state}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="mainImage" className="block text-sm font-medium text-gray-700 mb-1">
                Temple Image
              </label>
              <div className="flex items-center space-x-4">
                {imagePreview && (
                  <div className="h-20 w-20 overflow-hidden rounded-md">
                    <img
                      src={imagePreview || "/placeholder.svg"}
                      alt="Temple preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    id="mainImage"
                    name="mainImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                    disabled={uploadingImage}
                  />
                  {uploadingImage && (
                    <div className="mt-2 flex items-center">
                      <div className="animate-spin h-4 w-4 border-t-2 border-spiritual-600 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-500">Uploading image...</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Recommended size: 800x600 pixels, max 2MB</p>
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              placeholder="Provide a brief description of the temple"
            ></textarea>
          </div>

          <div className="mt-6">
            <label htmlFor="history" className="block text-sm font-medium text-gray-700 mb-1">
              History
            </label>
            <textarea
              id="history"
              name="history"
              rows="4"
              value={formData.history}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              placeholder="Provide historical information about the temple"
            ></textarea>
          </div>
        </div>

        {/* Location Map Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Temple Location</h2>
          <p className="text-gray-600 mb-4">Click on the map to set the exact location of the temple</p>

          <div className="h-96 w-full rounded-lg overflow-hidden mb-4">
            <LoadScript googleMapsApiKey="AIzaSyDVp22TSezfAcdYUa8hZOZlCcHxCLZNu9A">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={10}
                onClick={handleMapClick}
              >
                <Marker position={mapCenter} />
              </GoogleMap>
            </LoadScript>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="location.coordinates.lat" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                id="location.coordinates.lat"
                name="location.coordinates.lat"
                value={formData.location.coordinates.lat}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              />
            </div>
            <div>
              <label htmlFor="location.coordinates.lng" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                id="location.coordinates.lng"
                name="location.coordinates.lng"
                value={formData.location.coordinates.lng}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              />
            </div>
          </div>
        </div>

        {/* Opening Hours Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Opening Hours</h2>

          <div>
            <label htmlFor="timings" className="block text-sm font-medium text-gray-700 mb-1">
              Timings
            </label>
            <textarea
              id="timings"
              name="timings"
              rows="3"
              value={formData.timings}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
              placeholder="e.g., Summer: 6:00 AM to 9:00 PM, Winter: 7:00 AM to 8:00 PM, Aarti timings: Morning 4:00 AM to 7:00 AM, Evening 5:00 PM to 8:00 PM"
            ></textarea>
          </div>
        </div>

        {/* Festivals Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Festivals</h2>

          <div className="space-y-6">
            {formData.festivals.map((festival, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800">Festival #{index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => handleRemoveArrayItem(index, "festivals")}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Festival Name</label>
                    <input
                      type="text"
                      value={festival.name || ""}
                      onChange={(e) => handleArrayChange(index, "name", e.target.value, "festivals")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                      placeholder="e.g., Maha Shivaratri"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month/Time</label>
                    <input
                      type="text"
                      value={festival.date || ""}
                      onChange={(e) => handleArrayChange(index, "date", e.target.value, "festivals")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                      placeholder="e.g., February/March"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={festival.description || ""}
                    onChange={(e) => handleArrayChange(index, "description", e.target.value, "festivals")}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                    placeholder="Describe the festival and its significance"
                  ></textarea>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => handleAddArrayItem("festivals", { name: "", date: "", description: "" })}
              className="inline-flex items-center text-spiritual-600 hover:text-spiritual-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Festival
            </button>
          </div>
        </div>

        {/* Nearby Attractions Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Nearby Attractions</h2>

          <div className="space-y-6">
            {formData.nearbyAttractions.map((attraction, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800">Attraction #{index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => handleRemoveArrayItem(index, "nearbyAttractions")}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Attraction Name</label>
                    <input
                      type="text"
                      value={attraction.name || ""}
                      onChange={(e) => handleArrayChange(index, "name", e.target.value, "nearbyAttractions")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                      placeholder="e.g., Bhairavnath Temple"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
                    <input
                      type="text"
                      value={attraction.distance || ""}
                      onChange={(e) => handleArrayChange(index, "distance", e.target.value, "nearbyAttractions")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                      placeholder="e.g., 2 km"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={attraction.description || ""}
                    onChange={(e) => handleArrayChange(index, "description", e.target.value, "nearbyAttractions")}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                    placeholder="Describe the attraction"
                  ></textarea>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => handleAddArrayItem("nearbyAttractions", { name: "", distance: "", description: "" })}
              className="inline-flex items-center text-spiritual-600 hover:text-spiritual-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Nearby Attraction
            </button>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Additional Information</h2>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="architecture" className="block text-sm font-medium text-gray-700 mb-1">
                Architecture
              </label>
              <textarea
                id="architecture"
                name="architecture"
                rows="3"
                value={formData.architecture}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                placeholder="Describe the architectural style and features of the temple"
              ></textarea>
            </div>

            <div>
              <label htmlFor="importance" className="block text-sm font-medium text-gray-700 mb-1">
                Religious Importance
              </label>
              <textarea
                id="importance"
                name="importance"
                rows="3"
                value={formData.importance}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                placeholder="Describe the religious significance of the temple"
              ></textarea>
            </div>

            <div>
              <label htmlFor="visitingTips" className="block text-sm font-medium text-gray-700 mb-1">
                Visiting Tips
              </label>
              <textarea
                id="visitingTips"
                name="visitingTips"
                rows="3"
                value={formData.visitingTips}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-spiritual-500 focus:border-spiritual-500"
                placeholder="Provide helpful tips for visitors (best time to visit, what to bring, etc.)"
              ></textarea>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-spiritual-600 focus:ring-spiritual-500 border-gray-300 rounded"
              />
              <label htmlFor="featured" className="ml-2 block text-sm text-gray-700">
                Feature this temple on the homepage
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-md font-medium transition-colors"
          >
            Cancel
          </button>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={saving}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${saving ? "bg-gray-400 cursor-not-allowed" : "bg-gray-600 hover:bg-gray-700 text-white"
                }`}
            >
              Save as Draft
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${saving ? "bg-spiritual-400 cursor-not-allowed" : "bg-spiritual-600 hover:bg-spiritual-700 text-white"
                }`}
            >
              {isEditMode ? "Update Temple" : "Publish Temple"}
            </button>
          </div>
        </div>
      </form>

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6">
            <div className="text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto text-yellow-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Unsaved Changes</h3>
              <p className="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to leave this page?</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowUnsavedChangesModal(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Stay on Page
                </button>
                <button
                  onClick={() => {
                    setShowUnsavedChangesModal(false)
                    navigate("/admin/temples")
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default TempleForm
